// BandHowToTV.jsx  →  same folder as MagicBandTV.jsx
// "MagicBeacon Tour" for Band+ on the TV (remote-driven).
// Same guides, text and images as the display tour (imported from
// BandHowTo.jsx), except Sync, which gets its own TV slides below.
//
// Remote:
//   Tile screen:  Left/Right = tiles · Up = ✕ · OK = open guide · Back = close tour
//   Slides:       Left/Right = Previous/Next · Up = top bar (‹ / ✕) · Down = back to Next
//                 OK = press · Back = return to the tile screen
// Focus is trapped inside while open; the parent puts the remote back on
// the How To tab when onClose fires.

import React, { useState, useRef, useEffect } from "react";
import { GUIDES as DISPLAY_GUIDES, Icon } from "./BandHowTo";

const IMG = "/static/bandhowto/";
const SARAH_SRC = "/static/castmember-one.png";
const TOUR_VOICE_SRC = "/static/voice-tour.mp3"; // Sarah's line — silent until the file exists
const IMPORTANT_VOICE_SRC = "/static/voice-important.mp3"; // Sarah's IMPORTANT-page line — silent until the file exists
const OK_KEYS = new Set(["Enter", " "]);
const ARROWS = new Set(["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"]);
const isBackKey = (e) =>
  e.key === "Escape" || e.key === "GoBack" || e.key === "BrowserBack" ||
  e.keyCode === 461 || e.keyCode === 10009;

// ---- TV-only Sync guide (placeholder until we build it) ----
const DS = DISPLAY_GUIDES.find((g) => g.key === "sync").slides;
const TV_SYNC_SLIDES = [
  DS[0],
  DS[1],
  {
    title: "Sync Your Band!",
    body: <>5 minutes before showtime, the top bar will say <b>SYNC YOUR BAND</b>. Press <b>Up</b> on your remote to highlight it, then press <b>OK</b>. If more than one streamer is covering the show, you can pick your favorite.</>,
    video: IMG + "tv-sync-3.mp4",
  },
  {
    title: "Running Late?",
    body: <>If the show already started, the top bar may say <b>"Syncing to show"</b> with a countdown. Once it turns <b>green</b> and says <b>"Connected! CLICK HERE!"</b>, press <b>Up</b> to highlight the bar then <b>OK</b> to jump in mid-show.</>,
    video: IMG + "tv-sync-4.mp4",
  },
  DS[4],
  DS[5],
];

// TV wording for Colors slide 2 ("Pick Your Colors") — display keeps its own.
const TV_COLOR_SLIDE_2 = <>For ONE, just select your color. For TWO or THREE+, <b>assign a color to each zone on the band</b>. The band on screen shows your design.</>;

const GUIDES = DISPLAY_GUIDES.map((g) => {
  if (g.key === "sync") return { ...g, slides: TV_SYNC_SLIDES };
  if (g.key === "color")
    return { ...g, slides: g.slides.map((s, k) => (k === 1 ? { ...s, body: TV_COLOR_SLIDE_2 } : s)) };
  return g;
});

const CSS = `
.bhtv-back{position:fixed;inset:0;z-index:10050;background:rgba(10,14,28,.86);display:flex;align-items:center;justify-content:center;font-family:'Lato',sans-serif;}
.bhtv-card{position:relative;width:1720px;max-width:94vw;max-height:96vh;background:#fff;border-radius:30px;overflow:hidden;color:#1a2542;box-shadow:0 16px 50px rgba(10,14,28,.45);}
.bhtv-bar{display:flex;align-items:center;gap:22px;background:#007bff;padding:18px 28px 18px 40px;min-height:112px;box-sizing:border-box;}
.bhtv-rainbow{height:8px;background:linear-gradient(90deg,#FF5C93,#FF8A3D,#FFD23D,#B6E04A,#3EE08A,#3CC9FF,#6FB0FF,#8F9BFF,#B48CFF,#E07CFF,#FF6FC8);}
.bhtv-bartitle{flex:1;font-size:44px;font-weight:900;color:#fff;letter-spacing:-.3px;}
.bhtv-round{flex:0 0 auto;width:74px;height:74px;border-radius:999px;border:4px solid rgba(255,255,255,.7);background:transparent;color:#fff;font-family:'Lato',sans-serif;font-weight:900;line-height:1;display:flex;align-items:center;justify-content:center;cursor:pointer;padding:0;outline:none;transition:transform .12s ease,box-shadow .12s ease;}
.bhtv-round.back{font-size:44px;padding-bottom:5px;}
.bhtv-round.x{font-size:32px;}
.bhtv-round:focus{border-color:#05f651;background:rgba(5,246,81,.18);box-shadow:0 0 0 5px #05f651,0 0 30px rgba(5,246,81,.55);transform:scale(1.08);}
.bhtv-body-wrap{padding:36px 48px 34px;}
.bhtv-intro{font-size:40px;font-weight:700;color:#1a2542;margin:0 0 30px;}
.bhtv-tiles{display:grid;grid-template-columns:repeat(4,1fr);gap:28px;}
.bhtv-tile{height:380px;border-radius:24px;border:5px solid #d8dce3;background:#fff;color:#1a2542;font-family:'Lato',sans-serif;cursor:pointer;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:26px;padding:20px;text-align:center;outline:none;transition:transform .12s ease,box-shadow .12s ease,border-color .12s ease;}
.bhtv-tile:focus{border-color:#05f651;box-shadow:0 0 0 5px #05f651,0 0 34px rgba(5,246,81,.5);transform:scale(1.04);}
.bhtv-ico{width:128px;height:128px;border-radius:30px;background:#007bff;color:#fff;display:flex;align-items:center;justify-content:center;}
.bhtv-ico svg{width:74px;height:74px;}
.bhtv-tile-label{font-size:34px;font-weight:900;line-height:1.2;}
.bhtv-row{display:flex;gap:44px;align-items:stretch;}
.bhtv-shot{position:relative;flex:0 0 960px;height:600px;border-radius:18px;overflow:hidden;border:2px solid #d8dce3;background:#f1f3f7;}
.bhtv-shot img,.bhtv-shot video{display:block;width:100%;height:100%;object-fit:cover;}
.bhtv-noshot{flex:0 0 960px;height:600px;border-radius:18px;border:3px dashed #d8dce3;background:#f7f8fa;display:flex;align-items:center;justify-content:center;font-size:30px;font-weight:700;color:#8a93a6;}
.bhtv-text{flex:1;display:flex;flex-direction:column;justify-content:center;min-width:0;}
.bhtv-count{font-size:30px;font-weight:900;color:#007bff;margin:0 0 12px;}
.bhtv-title{font-size:52px;font-weight:900;line-height:1.1;margin:0 0 20px;color:#1a2542;letter-spacing:-.5px;}
.bhtv-body{font-size:32px;font-weight:400;line-height:1.45;color:#46506b;margin:0;}
.bhtv-body b{color:#1a2542;font-weight:900;}
.bhtv-body span{font-size:24px!important;}
.bhtv-nav{display:flex;align-items:center;justify-content:space-between;margin-top:30px;}
.bhtv-btn{min-width:300px;height:90px;border-radius:18px;border:4px solid #d8dce3;background:#fff;color:#1a2542;font-family:'Lato',sans-serif;font-size:32px;font-weight:900;cursor:pointer;outline:none;transition:transform .12s ease,box-shadow .12s ease;}
.bhtv-next{border-color:#007bff;background:#007bff;color:#fff;}
.bhtv-btn:focus{border-color:#05f651;box-shadow:0 0 0 5px #05f651,0 0 30px rgba(5,246,81,.5);transform:scale(1.05);}
.bhtv-dots{display:flex;gap:12px;}
.bhtv-pip{width:14px;height:14px;border-radius:999px;background:#d8dce3;}
.bhtv-pip.on{width:40px;background:#007bff;}
.bhtv-guide{display:flex;gap:18px;align-items:flex-end;margin:-6px 0 -34px -28px;}
.bhtv-sarah{flex:0 0 330px;height:500px;overflow:hidden;}
.bhtv-sarah img{display:block;width:100%;height:auto;}
.bhtv-guide-right{flex:1;min-width:0;padding-bottom:34px;}
.bhtv-bubble{position:relative;background:#fff;border:4px solid #1a2542;border-radius:24px;padding:20px 28px;font-size:32px;font-weight:700;line-height:1.35;color:#1a2542;margin:0 0 28px 10px;}
.bhtv-bubble:before{content:"";position:absolute;left:-22px;top:30px;border:18px solid transparent;border-right-color:#1a2542;border-left:0;}
.bhtv-bubble:after{content:"";position:absolute;left:-15px;top:33px;border:15px solid transparent;border-right-color:#fff;border-left:0;}
.bhtv-guide .bhtv-tiles{gap:22px;}
.bhtv-guide .bhtv-tile{height:320px;gap:14px;padding:18px;background:#f7f9fc;}
.bhtv-guide .bhtv-tile:focus{background:#fff;}
.bhtv-guide .bhtv-ico{width:104px;height:104px;border-radius:26px;}
.bhtv-guide .bhtv-ico svg{width:60px;height:60px;}
.bhtv-guide .bhtv-tile-label{font-size:30px;}
.bhtv-foot{border-top:2px solid #e6e9ef;padding:20px 48px;text-align:center;font-size:26px;color:#6b7590;background:#fafbfd;}
.bhtv-foot b{color:#007bff;}
.bhtv-note{background:#fff;border:4px solid #007bff;border-radius:24px;padding:26px 32px;}
.bhtv-note-head{display:inline-block;background:#e5484d;color:#fff;font-size:22px;font-weight:900;letter-spacing:3px;border-radius:10px;padding:7px 16px;margin-bottom:18px;}
.bhtv-note-row{display:flex;align-items:center;gap:20px;font-size:31px;line-height:1.35;color:#1a2542;margin:0 0 14px;}
.bhtv-note-row b{font-weight:900;}
.bhtv-note-num{flex:0 0 auto;width:50px;height:50px;border-radius:50%;background:#1a2542;color:#fff;font-size:26px;font-weight:900;display:flex;align-items:center;justify-content:center;}
.bhtv-note-btn{display:block;margin:20px 0 0 auto;padding:0 34px;}
`;

export default function BandHowToTV({ onClose }) {
  const [g, setG] = useState(null); // null = tile screen
  const [i, setI] = useState(0);
  const [broken, setBroken] = useState({});
  const [focus, setFocus] = useState("tile0"); // tileN | x | back | prev | next
  const refs = useRef({});
  const lastTileRef = useRef(0);

  const guide = GUIDES.find((x) => x.key === g);
  
    // Opens on the IMPORTANT page; "Got It" moves to the feature tiles.
  const [intro, setIntro] = useState(true);

  // Sarah's line for each screen (silent if the mp3 isn't there yet).
  useEffect(() => {
    let a;
    try { a = new Audio(intro ? IMPORTANT_VOICE_SRC : TOUR_VOICE_SRC); a.volume = 0.9; const p = a.play(); if (p && p.catch) p.catch(() => {}); } catch (e) {}
    return () => { try { if (a) a.pause(); } catch (e) {} };
  }, [intro]);
  const last = guide ? i === guide.slides.length - 1 : false;

  const reg = (name) => (el) => { refs.current[name] = el; };
  const focusOn = (name) => { setFocus(name); refs.current[name]?.focus(); };

  // Land the remote whenever the screen changes (open, guide in, guide out).
  useEffect(() => {
    const t = setTimeout(() => {
            if (!guide) focusOn(intro ? "gotit" : "tile" + lastTileRef.current);
      else focusOn("next");
    }, 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [g, intro]);

  const openGuide = (idx) => { lastTileRef.current = idx; setI(0); setG(GUIDES[idx].key); };
  const backToTiles = () => setG(null);
  const goPrev = () => (i === 0 ? backToTiles() : setI(i - 1));
  const goNext = () => (last ? backToTiles() : setI(i + 1));

  const activate = (name) => {
    if (name === "x") return onClose();
    if (name === "gotit") return setIntro(false);
    if (name === "back") return backToTiles();
    if (name === "prev") return goPrev();
    if (name === "next") return goNext();
    if (name.startsWith("tile")) return openGuide(Number(name.slice(4)));
  };

  const handleKey = (e) => {
    e.stopPropagation(); // the shell's tab handler never sees tour keys
    const k = e.key;
    if (isBackKey(e)) { e.preventDefault(); guide ? backToTiles() : onClose(); return; }
    if (OK_KEYS.has(k)) { e.preventDefault(); activate(focus); return; }
    if (!ARROWS.has(k)) return;
    e.preventDefault();
    if (!guide && intro) {
      // [ ✕ ] above [ Got It! ]
      if (k === "ArrowUp") focusOn("x");
      if (k === "ArrowDown" || k === "ArrowLeft") focusOn("gotit");
      return;
    }
    if (!guide) {
      // [ ✕ ] above [ tile0 tile1 tile2 tile3 ]
      if (focus === "x") {
        if (k === "ArrowDown" || k === "ArrowLeft") focusOn("tile" + lastTileRef.current);
        return;
      }
      const t = Number(focus.slice(4)) || 0;
      if (k === "ArrowLeft" && t > 0) focusOn("tile" + (t - 1));
      if (k === "ArrowRight" && t < GUIDES.length - 1) focusOn("tile" + (t + 1));
      if (k === "ArrowUp") focusOn("x");
      return;
    }

    // [ ‹ ] ........ [ ✕ ]
    // [ Previous ] .. [ Next ]
    if (focus === "back" || focus === "x") {
      if (k === "ArrowLeft") focusOn("back");
      if (k === "ArrowRight") focusOn("x");
      if (k === "ArrowDown") focusOn("next");
      return;
    }
    if (k === "ArrowLeft") focusOn("prev");
    if (k === "ArrowRight") focusOn("next");
    if (k === "ArrowUp") focusOn("x");
  };

  // Keep the remote inside the tour (anything that pulls focus out gets yanked back).
  const trapBlur = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) refs.current[focus]?.focus();
  };

  // ---------- Tile screen ----------
  if (!guide) {
    return (
      <div className="bhtv-back" onKeyDown={handleKey} onBlurCapture={trapBlur}>
        <style>{CSS}</style>
        <div className="bhtv-card">
          <div className="bhtv-bar">
            <div className="bhtv-bartitle">Welcome to the MagicBeacon Tour!</div>
            <button ref={reg("x")} className="bhtv-round x" tabIndex={0} onFocus={() => setFocus("x")} onClick={onClose} aria-label="Close">✕</button>
          </div>
          <div className="bhtv-rainbow" />
          <div className="bhtv-body-wrap">
                        <div className="bhtv-guide">
              <div className="bhtv-sarah"><img key="sarah" src={SARAH_SRC} alt="" draggable="false" /></div>
              <div className="bhtv-guide-right">
                <div className="bhtv-bubble">{intro ? "Hi, I'm Sarah! Before we start, two important things about your MagicBands..." : "Now pick a feature and I'll walk you through how to use it!"}</div>
                {intro && (
                  <div className="bhtv-note">
                    <div className="bhtv-note-head">IMPORTANT</div>
                    <div className="bhtv-note-row"><span className="bhtv-note-num">1</span><span>MagicBands must be <b>fully charged</b> for optimal performance.</span></div>
                    <div className="bhtv-note-row"><span className="bhtv-note-num">2</span><span>MagicBands <b>will not work while charging</b> or while clipped into the charger.</span></div>
                    <button ref={reg("gotit")} className="bhtv-btn bhtv-next bhtv-note-btn" tabIndex={0} onFocus={() => setFocus("gotit")} onClick={() => setIntro(false)}>Got It! Show Me Around ›</button>
                  </div>
                )}
                <div className="bhtv-tiles" style={intro ? { display: "none" } : undefined}>
                  {GUIDES.map((t, idx) => (
                    <button
                      key={t.key}
                      ref={reg("tile" + idx)}
                      className="bhtv-tile"
                      tabIndex={0}
                      onFocus={() => { setFocus("tile" + idx); lastTileRef.current = idx; }}
                      onClick={() => openGuide(idx)}
                    >
                      <span className="bhtv-ico"><Icon name={t.icon} /></span>
                      <span className="bhtv-tile-label">{t.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="bhtv-foot">You can reopen this guide anytime from the <b>"How To"</b> button on the menu bar.</div>
        </div>
      </div>
    );
  }

  // ---------- Guide slides ----------
  const s = guide.slides[i];
  const showImg = s.img && !broken[s.img];

  return (
    <div className="bhtv-back" onKeyDown={handleKey} onBlurCapture={trapBlur}>
      <style>{CSS}</style>
      <div className="bhtv-card">
        <div className="bhtv-bar">
          <button ref={reg("back")} className="bhtv-round back" tabIndex={0} onFocus={() => setFocus("back")} onClick={backToTiles} aria-label="Back to the tour">‹</button>
          <div className="bhtv-bartitle">{guide.label}</div>
          <button ref={reg("x")} className="bhtv-round x" tabIndex={0} onFocus={() => setFocus("x")} onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="bhtv-rainbow" />
        <div className="bhtv-body-wrap">
          <div className="bhtv-row">
            {s.video ? (
              <div className="bhtv-shot">
                <video key={s.video} src={s.video} ref={(v) => { if (v) { v.muted = true; const p = v.play(); if (p && p.catch) p.catch(() => {}); } }} muted autoPlay loop playsInline preload="auto" disablePictureInPicture controls={false} tabIndex={-1} />
              </div>
            ) : showImg ? (
              <div className="bhtv-shot">
                <img key={s.img} src={s.img} alt="" draggable="false" onError={() => setBroken((b) => ({ ...b, [s.img]: true }))} />
              </div>
            ) : (
              <div className="bhtv-noshot">Screenshot coming soon</div>
            )}

            <div className="bhtv-text">
              <div className="bhtv-count">{i + 1}/{guide.slides.length}</div>
              <h2 className="bhtv-title">{s.title}</h2>
              <p className="bhtv-body">{s.body}</p>
            </div>
          </div>

          <div className="bhtv-nav">
            <button ref={reg("prev")} className="bhtv-btn" tabIndex={0} onFocus={() => setFocus("prev")} onClick={goPrev}>‹ Previous</button>
            <div className="bhtv-dots">
              {guide.slides.map((_, k) => (
                <span key={k} className={"bhtv-pip" + (k === i ? " on" : "")} />
              ))}
            </div>
            <button ref={reg("next")} className="bhtv-btn bhtv-next" tabIndex={0} onFocus={() => setFocus("next")} onClick={goNext}>
              {last ? "Got It!" : "Next ›"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
